import { useInView } from 'react-intersection-observer';

import { styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { MetaData } from 'Components/MetaData';

import SecondaryNavBar from 'Blocks/SecondaryNavBar/SecondaryNavBar';
import { StylingGelTestimonies } from 'Blocks/StylingGelTestimonies';

import ScrollTracker from 'Containers/ScrollTracker';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import {
  altHeroImage1,
  altHeroImage2,
  heroImage1,
  heroImage2,
} from 'assets/content/landingPages/stylingGel';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { AboutStylingGelSection } from './StylingGel/AboutStylingGelSection';
import { CTA } from './StylingGel/CTA';
import { DreamTeam } from './StylingGel/DreamTeam';
import HeroSection from './StylingGel/HeroSection';
import { HowProseWorksSection } from './StylingGel/HowProseWorksSection';
import { ProductStylingGelSection } from './StylingGel/ProductStylingGelSection';
import { StylingGelFAQ } from './StylingGel/StylingGelFAQ';
import { StylingGelFormula } from './StylingGel/StylingGelFormula';
import { StylingGelHowToVideos } from './StylingGel/StylingGelHowToVideos';

const StyledButtonFooter = styled(CTA)`
  left: 0;
  width: 100%;
  bottom: 0;
  margin: 0;
  z-index: 100;
  position: fixed;
`;

const HeroImage = styled.div`
  width: 100%;
`;

const Image = styled.img`
  display: block;
  object-fit: cover;
  width: 100%;
`;

// without the align-self to normal the dream team section breaks the mobile page
const StyledDreamTeamSection = styled('section')`
  align-self: normal;
`;

const setArtDirectedHeroImage = (
  heroImageSizes: Array<{
    image: StaticImageData;
    breakpoint: string;
  }>,
  defaultImageSize: StaticImageData,
) => (
  <HeroImage>
    <picture>
      {heroImageSizes.map(({ image, breakpoint }) => (
        <source key={image.src} media={breakpoint} srcSet={image.src} />
      ))}
      <Image alt="" src={defaultImageSize.src} />
    </picture>
  </HeroImage>
);

type StylingGelLandingProps = {
  header: boolean;
  footer: boolean;
  banner: boolean;
  variant: 'curl' | 'coily' | null;
};

const StylingGelLanding = ({
  header = true,
  footer = true,
  banner = true,
  variant = null,
}: StylingGelLandingProps) => {
  const isCoilyVariant = variant === 'coily';
  const { isMobile } = useResponsiveVariant();

  const heroImage1Mobile = !isCoilyVariant
    ? heroImage1.map((heroImage) => heroImage.image)[0]
    : altHeroImage1.map((heroImage) => heroImage.image)[0];
  const heroImage2Mobile = !isCoilyVariant
    ? heroImage2.map((heroImage) => heroImage.image)[0]
    : altHeroImage2.map((heroImage) => heroImage.image)[0];

  const intersectOption = {
    threshold: 0.2,
  };
  const [refStylingGelLpAbout, inViewRefAboutSection] = useInView(intersectOption);
  const [refStylingGelLpVideo, inViewRefVideo] = useInView(intersectOption);
  const [refStylingGelLpWorks, inViewRefHowProseWorks] = useInView(intersectOption);
  const [refStylingGelLpFormula, inViewRefFormula] = useInView(intersectOption);
  const [refStylingGelLpDreamTeam, inViewRefDreamTeam] = useInView(intersectOption);
  const [refStylingGelLpFaq, inViewRefFaq] = useInView(intersectOption);
  const [refStylingGelLpTestimonies, inViewRefTestimonies] = useInView(intersectOption);

  const navEntries = [
    {
      testId: 'stylingGelLpAbout',
      label: 'About Styling Gel',
      isActive: inViewRefAboutSection,
      id: 'about',
      dataClick: 'about',
    },
    {
      testId: 'stylingGelLpWorks',
      label: 'How Prose Works',
      isActive: !inViewRefAboutSection && inViewRefHowProseWorks,
      id: 'how-prose-works',
      dataClick: 'how-prose-works',
    },
    {
      testId: 'stylingGelLpTestimonies',
      label: 'Hair tips & tricks',
      isActive: !inViewRefHowProseWorks && inViewRefTestimonies,
      id: 'testimonies',
      dataClick: 'testimonials',
    },
    {
      testId: 'stylingGelLpVideo',
      label: 'How to change up your look',
      isActive: !inViewRefTestimonies && inViewRefVideo,
      id: 'watch',
      dataClick: 'style',
    },
    {
      testId: 'stylingGelLpFormula',
      label: 'Natural Wonders',
      isActive: !inViewRefVideo && inViewRefFormula,
      id: 'formula',
      dataClick: 'ingredients',
    },
    {
      testId: 'stylingGelLpDreamTeam',
      label: 'Advisory Board',
      isActive: !inViewRefFormula && inViewRefDreamTeam,
      id: 'dream-team',
      dataClick: 'experts',
    },
    {
      testId: 'stylingGelLpFaq',
      label: 'FAQs',
      isActive: !inViewRefDreamTeam && inViewRefFaq,
      id: 'faq',
      dataClick: 'faq',
    },
  ];

  return (
    <>
      <MetaData
        description="What is styling gel? How can you use hair gel? Is styling gel bad for you? Find answers here and see why Prose custom styling gel is the best gel for you."
        inheritParentCanonicalURL
        title="Styling Gel FAQs: Learn About Our Custom Hair Gel | Prose"
      />

      <BaseScene background="none" banner={banner} footer={footer} header={header}>
        <ScrollTracker>
          <HeroSection />
          <SecondaryNavBar
            dataTestId="suppLpNavSecondary"
            entries={navEntries}
            scrollMarginTop="95px"
          />
          <section ref={refStylingGelLpAbout} data-testid="stylingGelLpAbout" id="about">
            <AboutStylingGelSection variant={variant} />
            <ProductStylingGelSection />
          </section>
          <section ref={refStylingGelLpWorks} data-testid="stylingGelLpWorks" id="how-prose-works">
            <HowProseWorksSection />
          </section>
          <section
            ref={refStylingGelLpTestimonies}
            data-testid="stylingGelLpTestimonies"
            id="testimonies"
          >
            <StylingGelTestimonies variant={variant} />
          </section>
          {setArtDirectedHeroImage(!isCoilyVariant ? heroImage1 : altHeroImage1, heroImage1Mobile!)}
          <section ref={refStylingGelLpVideo} data-testid="stylingGelLpVideo" id="watch">
            <StylingGelHowToVideos variant={variant} />
          </section>
          <StylingGelFormula
            ref={refStylingGelLpFormula}
            dataTestId="stylingGelLpFormula"
            id="formula"
            isCoilyVariant={isCoilyVariant}
          />
          {setArtDirectedHeroImage(!isCoilyVariant ? heroImage2 : altHeroImage2, heroImage2Mobile!)}
          <StyledDreamTeamSection
            ref={refStylingGelLpDreamTeam}
            data-testid="stylingGelLpDreamTeam"
            id="dream-team"
          >
            <DreamTeam />
          </StyledDreamTeamSection>
          <StylingGelFAQ ref={refStylingGelLpFaq} dataTestId="stylingGelLpFaq" />
          {isMobile && (
            <StyledButtonFooter
              dataFrom="styling-gel-sticky-cta"
              dataTestId="styling-gel-lp-cta"
              variant="vert"
            >
              get your styling gel
            </StyledButtonFooter>
          )}
        </ScrollTracker>
      </BaseScene>
    </>
  );
};

export default StylingGelLanding;
